/**
 * The maths behind the floating sticker card. Pure functions only: the
 * showcase runs them inside one motion `frame.update` loop and writes the
 * results into motion values, which render as transforms and opacities.
 */

/** The idle drift: a slow, never-repeating-looking yaw/pitch/float. */
export const IDLE_MOTION = {
  yawDeg: 13,
  pitchDeg: 5,
  floatPx: 8,
  // Incommensurate periods, so the three axes never line up into an
  // obvious loop.
  yawPeriodS: 7.3,
  pitchPeriodS: 5.1,
  floatPeriodS: 4.2,
} as const;

/** How far a pointer or finger at the card's edge turns it. */
export const INPUT_TILT = { yawDeg: 20, pitchDeg: 15 } as const;

/** The resting pose when nothing may move: turned just enough to read as 3D. */
export const STATIC_POSE: StickerPose = { yaw: -9, pitch: 4, lift: 0.5 };

export interface StickerPose {
  /** rotateY, degrees. Positive turns the right edge away. */
  yaw: number;
  /** rotateX, degrees. Positive tips the top edge away. */
  pitch: number;
  /** 0 at the bottom of the float, 1 at the top. */
  lift: number;
}

const TAU = Math.PI * 2;

export function idlePose(seconds: number, amplitude = 1): StickerPose {
  const yaw =
    Math.sin((seconds / IDLE_MOTION.yawPeriodS) * TAU) * IDLE_MOTION.yawDeg;
  const pitch =
    Math.sin((seconds / IDLE_MOTION.pitchPeriodS) * TAU + 1.1) *
    IDLE_MOTION.pitchDeg;
  const lift =
    0.5 + 0.5 * Math.sin((seconds / IDLE_MOTION.floatPeriodS) * TAU + 0.4);
  return { yaw: yaw * amplitude, pitch: pitch * amplitude, lift };
}

export interface SpringState {
  value: number;
  velocity: number;
}

/**
 * One semi-implicit Euler step of a damped spring towards `target`.
 * `dt` is clamped so a tab that comes back after a pause does not explode.
 */
export function stepSpring(
  state: SpringState,
  target: number,
  dtSeconds: number,
  stiffness = 140,
  damping = 15,
): SpringState {
  const dt = Math.min(Math.max(dtSeconds, 0), 1 / 30);
  const force = stiffness * (target - state.value) - damping * state.velocity;
  const velocity = state.velocity + force * dt;
  return { value: state.value + velocity * dt, velocity };
}

/** Frame-rate independent exponential approach. */
export function approach(
  current: number,
  target: number,
  dtSeconds: number,
  timeConstantS: number,
): number {
  if (timeConstantS <= 0) return target;
  return (
    current + (target - current) * (1 - Math.exp(-dtSeconds / timeConstantS))
  );
}

/** Every layer value the card needs for one pose. */
export interface StickerLayers {
  rotateX: number;
  rotateY: number;
  /** Card translateY, px. */
  y: number;
  /** The blob bobs a beat behind the card, px. */
  blobY: number;
  /** Cast shadow, px / scale / opacity. It moves opposite the turn. */
  shadowX: number;
  shadowScale: number;
  shadowOpacity: number;
  /** Holographic sheen position (% of card width) and strength. */
  sheenX: number;
  sheenOpacity: number;
  /** Specular glint offset, as a fraction of the blob size. */
  glintX: number;
  glintY: number;
  /** Thin-film tint over the blob face. */
  film: number;
  filmX: number;
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

export function layersForPose(
  pose: StickerPose,
  seconds: number,
): StickerLayers {
  const turn = clamp01(Math.hypot(pose.yaw / 22, pose.pitch / 16));
  return {
    rotateX: pose.pitch,
    rotateY: pose.yaw,
    y: -pose.lift * IDLE_MOTION.floatPx,
    blobY: -Math.sin(seconds * 1.3 + 0.8) * 3,
    // Light from the upper left, so the shadow rests down and to the right,
    // and slides the other way to the turn.
    shadowX: 10 - pose.yaw * 1.4,
    shadowScale: 1 - pose.lift * 0.1,
    shadowOpacity: 0.95 - pose.lift * 0.3,
    sheenX: (pose.yaw / INPUT_TILT.yawDeg) * 85,
    sheenOpacity: 0.18 + turn * 0.62,
    glintX: -pose.yaw / 180,
    glintY: pose.pitch / 180,
    film: 0.08 + turn * 0.3,
    filmX: pose.yaw * -1.2,
  };
}

export type SwipeIntent = "next" | "prev" | "dismiss" | "settle";

export interface SwipeRelease {
  axis: "x" | "y" | null;
  dx: number;
  dy: number;
  /** px per ms */
  vx: number;
  vy: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const SWITCH_DISTANCE_PX = 70;
const SWITCH_VELOCITY = 0.45;
const DISMISS_DISTANCE_PX = 120;
const DISMISS_VELOCITY = 0.6;

/** What a finger lifting off the stage means. */
export function resolveSwipe(release: SwipeRelease): SwipeIntent {
  if (release.axis === "x") {
    // A deliberate reversal wins even after crossing the distance threshold.
    if (
      (release.dx < 0 && release.vx > SWITCH_VELOCITY) ||
      (release.dx > 0 && release.vx < -SWITCH_VELOCITY)
    )
      return "settle";
    const forward =
      release.dx < -SWITCH_DISTANCE_PX || release.vx < -SWITCH_VELOCITY;
    const back =
      release.dx > SWITCH_DISTANCE_PX || release.vx > SWITCH_VELOCITY;
    // A fling against the drag (dragged left, flicked right) cancels.
    if (forward && release.dx < 0 && release.hasNext) return "next";
    if (back && release.dx > 0 && release.hasPrev) return "prev";
    return "settle";
  }
  if (release.axis === "y") {
    if (release.vy < -DISMISS_VELOCITY) return "settle";
    if (
      release.dy > 0 &&
      (release.dy > DISMISS_DISTANCE_PX || release.vy > DISMISS_VELOCITY)
    ) {
      return "dismiss";
    }
  }
  return "settle";
}

/** Past the first or last sticker the card follows the finger reluctantly. */
export function rubberBand(offset: number, canMove: boolean): number {
  return canMove ? offset : offset * 0.3;
}
